/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {SequentiallyBetLineInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetLineInitialWPController";
import {Linear, Power2, Timeline, TimelineLite, TweenLite} from "gsap";
import {BonusBunniesInitialWPView} from "./BonusBunniesInitialWPView";
import {BonusBunniesInitialWinCountUp} from "./BonusBunniesInitialWinCountUp";
import {BonusBunniesGameConfig} from "../../BonusBunniesGameConfig";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {BonusBunniesParsedGameData} from "../../server/data/BonusBunniesParsedGameData";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {BonusBunniesGameModuleConfig} from "../../BonusBunniesGameModuleConfig";
import {BonusBunniesSoundConfig} from "../../BonusBunniesSoundConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {BonusBunnies} from "../../BonusBunnies";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {BonusBunniesIndividualWinAmountView} from "./BonusBunniesIndividualWinAmountView";

export class BonusBunniesInitialWPController extends SequentiallyBetLineInitialWPController {

    private readonly _symbolDelay:number = 0.1;

    private _gameView:BonusBunniesInitialWPView;
    private _winCountUp:BonusBunniesInitialWinCountUp;
    private _currentWinRatioConfig:IWinRatioConfig;
    private _allWinPositions:boolean[][];
    private _winAnimationMainTL:TimelineLite;
    private _individualWinView:BonusBunniesIndividualWinAmountView;
    private _betLineWinsData: IBetLineWinData[];

    constructor(indexes:[number, number]) {
        super(indexes);
        this._gameView = BonusBunniesGameModuleConfig.bonusBunniesInitialWPView = new BonusBunniesInitialWPView();
        this._winCountUp = new BonusBunniesInitialWinCountUp(BonusBunniesGameConfig.TEXT_STYLES.COUNTUP_YELLOW_BITMAP_STYLE);
        this._winCountUp.position.set(360, GameConfig.instance.GAME_HEIGHT * 0.55);
        this._individualWinView = new BonusBunniesIndividualWinAmountView();
        StageManager.getLayer(BonusBunniesGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._individualWinView,this._winCountUp);
    }

    protected parseFeatureGameData(data:BonusBunniesParsedGameData):void {
        this._currentWinRatioConfig = data.currentWinRatioConfig!;
        if(this.hasWin()) {
            this._allWinPositions = data.allWinPositions;
        }
        // #344
        BonusBunniesGameConfig.instance.WIN_BELOW_STAKE = (BonusBunnies.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));

        this._betLineWinsData = data.betLineWins;
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        if(this._gameData!.isBigWin) {
            return;
        }

        const totalWin:number = this._gameData!.totalWin;
        const from:number = totalWin - this._gameData!.singleWin;
        const duration:number = this._gameData!.currentWinRatioConfig!.duration;

        const countUpTimeline:TimelineLite = new TimelineLite({delay : 0.1});
        countUpTimeline.add(() => this._winCountUp.alpha = 0);
        countUpTimeline.add(() => this._winCountUp.scale.set(0, 0));
        if (BonusBunniesGameConfig.instance.WIN_BELOW_STAKE == false) {
            countUpTimeline.add([
                new TweenLite(this._winCountUp.scale, 0.3, {
                    x : this.getWinTypeScale(this._currentWinRatioConfig.type!),
                    y : this.getWinTypeScale(this._currentWinRatioConfig.type!),
                    ease : Power2.easeOut
                }),
                new TweenLite(this._winCountUp, 0.2, {alpha : 1, ease : Linear.easeOut}),
                () => this._winCountUp.currentWinRatio = this._currentWinRatioConfig.type!,
                () => {
                    if (duration > 0 && !BonusBunniesGameConfig.instance.WIN_BELOW_STAKE) {
                        SlotGame.sound.playEffect(BonusBunniesSoundConfig.instance.COUNTUP)
                    }
                },
                this._winCountUp.getCountUpAnimation(0, this._gameData!.singleWin, duration, this._gameData!.currentMultiplier)
            ]);
        }
        countUpTimeline.add(() => {
            // #344
            if(duration > 0 && !BonusBunniesGameConfig.instance.WIN_BELOW_STAKE) {
                SlotGame.sound.stopEffect(BonusBunniesSoundConfig.instance.COUNTUP);
                SlotGame.sound.playEffect(BonusBunniesSoundConfig.instance.COUNTUP_ENDS);
            }
        });

        timeline.add([
            () => {
                if(duration > 1) {
                    SlotGame.sound.playEffect(BonusBunniesSoundConfig.instance.COIN_FOUNTAIN);
                    this._gameView.playBronzeCoins((duration > 1.5) ? 1 : 0);
                }
            },
            countUpTimeline
        ], 0);
    }

    private getWinTypeSound(winType:WinRatioType):string {
        switch(winType) {
            case WinRatioType.BIG_WIN :
                return BonusBunniesSoundConfig.instance.BIG_WIN_INITIAL_WIN;

            case WinRatioType.MEGA_WIN :
                return BonusBunniesSoundConfig.instance.BIG_WIN_INITIAL_WIN;

            case WinRatioType.SUPER_MEGA_WIN :
                return BonusBunniesSoundConfig.instance.BIG_WIN_INITIAL_WIN;

            case WinRatioType.LARGE :
                return BonusBunniesSoundConfig.instance.WIN_LARGE;

            case WinRatioType.MEDIUM :
                return BonusBunniesSoundConfig.instance.WIN_MEDIUM;

            case WinRatioType.SMALL :
                return BonusBunniesSoundConfig.instance.WIN_SMALL;

            case WinRatioType.TINY :
                return BonusBunniesSoundConfig.instance.WIN_TINY;

            default :
                return "";
        }
    }

    private getWinTypeScale(winType:WinRatioType):number {
        switch(winType) {
            case WinRatioType.LARGE :
                return 1.10;

            case WinRatioType.MEDIUM :
                return 1.05;

            case WinRatioType.SMALL :
                return 1;

            case WinRatioType.TINY :
                return 0.95;

            default :
                return 1;
        }
    }

    public disableAllNoWinSymbols():void {
        for(let i:number = 0; i < this._allWinPositions.length; i++) {
            for(let j:number = 0; j < 4; j++) {
                if(this._allWinPositions[i][j] == false) {
                    this._gameView.setSymbolInitialWinNoWin((Reels.getSymbol(i, j)!), i, j, "win_not");
                }
            }
        }
    }

    public enableAllWinSymbols():void {
        for(let i:number = 0; i < this._allWinPositions.length; i++) {
            for(let j:number = 0; j < 4; j++) {
                if(this._allWinPositions[i][j] == true) {
                    this._gameView.setSymbolToState((Reels.getSymbol(i, j)!).symName, i, j);
                }
            }
        }
    }

    protected addWinAnimationsToTimeline(timeline:Timeline):void {
        if(this._winAnimationMainTL && this._winAnimationMainTL.isActive()) {
            this._winAnimationMainTL.pause();
            this._winAnimationMainTL.kill();
        }

        this._winAnimationMainTL = new TimelineLite();

        // Disable all not win symbols
        this.disableAllNoWinSymbols();

        //Disable all no win on row 4
        for(let j:number = 0; j < 4; j++) {
            this._gameView.setSymbolInitialWinNoWin((Reels.getSymbol(j, 4)!), j, 4, "win_not");
        }

        let delay:number = 0;
        if (!BonusBunniesGameConfig.instance.WIN_BELOW_STAKE) { // #344
            for (let i: number = 0; i < this._allWinPositions.length; i++) {
                const reelWinAnimations: TimelineLite[] = [];
                for (let j: number = 0; j < 4; j++) {
                    if (this._allWinPositions[i][j] == true) {
                        reelWinAnimations.push(this._gameView.getSymbolInitialWinTL((Reels.getSymbol(i, j)!), i, j, "win"));
                    }
                }
                if (i > 0) {
                    delay = 0.98;
                }
                this._winAnimationMainTL.add(reelWinAnimations, "-=" + delay);
                if(i===0){
                    // add individual win amount text
                    this._winAnimationMainTL.add(() => {
                        if (this._betLineWinsData && this._betLineWinsData.length > 0) {
                            if(BonusBunnies.api.gameClientConfiguration.displayIndividualWinsInGame){
                                this._individualWinView.displayIndividualBetLineAmount(this._betLineWinsData);
                            }
                        }
                    },0);
                }
            }
        }
        timeline.add([
            () => {
                if (!BonusBunniesGameConfig.instance.WIN_BELOW_STAKE) { // #344
                    SlotGame.sound.playEffect(this.getWinTypeSound(this._currentWinRatioConfig.type!))
                }
            },
            this._winAnimationMainTL
        ]);
    }

    protected getNextBetWinAnimationTimeOffset(lastSymbolDelay:number):number {
        return lastSymbolDelay;
    }

    protected getNextSymbolAnimationTimeOffset(duration:number):number {
        return duration;
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);
        if(this._gameData!.isBigWin == false) {
            SlotGame.winFieldController.showWinField();
        }
        this._individualWinView.individualLineAmountReset();
        if (BonusBunniesGameConfig.instance.WIN_BELOW_STAKE == false) {
            this._winCountUp.alpha = 1;
        }
        this._winCountUp.scale.set(this.getWinTypeScale(this._currentWinRatioConfig.type!), this.getWinTypeScale(this._currentWinRatioConfig.type!));
        this._winCountUp.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);

        this._gameView.abort();
        if(isAborted == true) {
            BonusBunniesGameModuleConfig.bonusBunniesInitialWPView.cleanupSpineWinSymbols();
            SlotGame.sound.stopEffect(BonusBunniesSoundConfig.instance.COUNTUP);
            SlotGame.sound.playEffect(BonusBunniesSoundConfig.instance.COUNTUP_ENDS);
        }
        if(SlotGame.autoPlay.isAutoplayRound) {
            this._gameView.cleanupSpineWinSymbols();
        }
    }
}